<template>
    <div class="border-gray-100 border overflow-hidden mb-4 proof-list-card">
        <div class="px-3 py-2 border-bottom border-gray-100 d-flex align-items-center justify-content-between gap-12">
            <div class="text-ashore-black fs-14 font-weight-bold">Associated Proofs</div>
            <button class="attach-proof-btn btn d-flex align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                @click="openAttachProofModal">
                <img src="/img/icon-attachment.svg" width="14" alt="file-attachment-icon">
                Attach Proof
            </button>
        </div>
        <b-card-body>
            <!-- Loading State -->
            <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border text-gray-400" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2 text-muted">Loading associated proofs...</p>
            </div>

            <!-- No Proofs State -->
            <div v-else-if="associatedProofs.length === 0" class="text-center no-proof-attached border-top border-gray-100">
                <p class="text-ashore-black fw-normal fs-14 mb-4">No associated proofs to job.</p>
                <button
                    class="attach-proof-btn btn justify-content-center align-items-center gap-2 fw-medium fs-12 px-12 py-2"
                    @click="openAttachProofModal">
                    <img src="/img/icon-attachment.svg" width="14" alt="file-attachment-icon">
                    Attach Proof
                </button>
            </div>

            <!-- Proof List -->
            <ul v-else class="job-associated-proof-container">
                <!-- Proof -->
                <li v-for="proof in associatedProofs" class="proof-list-item line-item-large" v-bind:key="proof.id">
                    <div class="row">
                        <!--   Proof Name -->
                        <div class="col-lg-8 col-md-12 proof-status">
                            <div>
                                <div class="d-flex justify-content-start proof-name-row">
                                    <span class="proof-title">
                                        {{ proof.name }}
                                    </span>
                                </div>

                                <div class="job-proof-label d-flex flex-column flex-lg-row justify-content-start align-items-center mt-2 gap-12">
                                    <b-badge variant="success" class="proof-status-badge">
                                        <span
                                            :class="`status-indicator ${getProofStatusClass(proof)}`"
                                            class="rounded-pill mr-2">
                                        </span>
                                        {{ getProofStatusTextDisplay(proof) }}
                                    </b-badge>
                                    <span class="proof-info-text">{{ getVersionInfo(proof) }}</span>
                                </div>
                            </div>
                        </div>
                        <!--  Avatars -->
                        <div
                            class="col-lg-2 col-md-12 pr-2 d-flex align-items-center justify-content-sm-start justify-content-lg-end">
                            <doc-type-avatars 
                                :proof="proof" 
                                :proof-status-colors="proofStatusColors"
                                avatar-size="32px" />
                        </div>
                        <div
                            class="col-lg-2 col-md-12 pl-2 d-flex justify-content-lg-around justify-content-sm-between">
                            <!-- View Proof Details -->
                            <a class="view-proof-details-btn w-100" :href="getProofItemLink(proof)"
                                target="_blank">View
                            </a>
                        </div>
                    </div>
                </li>
            </ul>
        </b-card-body>

        <!-- Attach Proof Modal -->
        <modalAttachProofs :job-id="jobId" @proofs-attached="onProofsAttached" />
    </div>
</template>

<script>
import modalAttachProofs from "../modals/modal-attach-proofs.vue";
import docTypeAvatars from "../global/doc-type-avatars.vue";
import { formatLocalDate, getProofStatusText, getProofStatusColor, initProofStatusColor, getProofStatusClass, getProofStatusTextDisplay, getVersionInfo } from "../../utils/helpers";
import { DOC_TYPES, PROOF_VERSION_STATUS, PROOF_STATUS_CLASS, PROOF_STATUS_TEXT, ROUTES } from "../../utils/constants";

const { PROOF_ITEM } = ROUTES;

export default {
    name: 'AssociatedProofs',
    components: {
        modalAttachProofs,
        docTypeAvatars
    },
    props: {
        jobId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            associatedProofs: [],
            isLoading: true,
            proofStatusColors: {},
            formatLocalDate,
            DOC_TYPES,
            PROOF_VERSION_STATUS,
            PROOF_STATUS_CLASS,
            PROOF_STATUS_TEXT,
            ROUTES
        };
    },
    async mounted() {
        await this.loadAssociatedProofs();
    },
    computed: {
        getProofStatusClass() {
            return (proof) => getProofStatusClass(proof, this.proofStatusColors);
        },
        getProofStatusTextDisplay() {
            return (proof) => getProofStatusTextDisplay(proof, this.proofStatusColors);
        },
        getVersionInfo() {
            return (proof) => getVersionInfo(proof, this.formatLocalDate);
        }
    },
    methods: {
        getProofStatusText,
        getProofStatusColor,
        initProofStatusColor,
        async loadAssociatedProofs() {
            try {
                this.isLoading = true;

                const jobResponse = await this.$A.JobService.getJobById(this.jobId);
                const jobProofLinks = jobResponse.proofs || [];
                if (jobProofLinks.length === 0) {
                    this.associatedProofs = [];
                    this.isLoading = false;
                    return;
                }
                const jobProofIds = jobProofLinks.map(link => link.proofId);

                // Filter server-side by id: the unfiltered search only returns the account's
                // 10 newest proofs, so older linked proofs would silently never display.
                this.associatedProofs = await this.$A.ProofService.GetProofsByFilter({ proofIds: jobProofIds }, 1);
                this.associatedProofs.forEach(proof => initProofStatusColor(proof, this.proofStatusColors));
            } catch (error) {
                this.$A.AlertUser('Failed to load associated proofs');
                this.associatedProofs = [];
            } finally {
                this.isLoading = false;
            }
        },
        getProofItemLink(proof) {
            return `${PROOF_ITEM}?id=${proof.id}`;
        },

        openAttachProofModal() {
            this.$bvModal.show('modalAttachProofs');
        },

        onProofsAttached(changeData) {
            this.loadAssociatedProofs();
        }
    }
}
</script>

<style scoped>
.no-proof-attached {
    padding: 32px;
}

.attach-proof-btn {
    border: 1px solid #E5E5E5;
    border-radius: 4px;
    color: #525252 !important;
}

.attach-proof-btn:hover {
    text-decoration: none;
    background: #E0E4E7;
}

.job-associated-proof-container {
    padding-left: 0px !important;
    margin-bottom: 0px !important;
}

.proof-list-card {
    border-radius: 12px;
}


.proof-status-badge {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px 13px;
    height: 26px;
    border: 1px solid #B5B5B8;
    border-radius: 13px;
    background-color: white;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #444444;
}

.proof-list-item {
    background: #ffffff;
    margin-bottom: 1px;
    padding: 1px 24px;
    overflow: auto;
    box-shadow: 0px 1px 3px rgb(8 11 13 / 10%), inset 0px 1px 2px rgb(8 11 13 / 6%);
}

.proof-list-item:last-child {
    margin-bottom: 0px;
}

.proof-info-text {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #707073;
}

.proof-title {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #444444;
}

.view-proof-details-btn {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 9px 17px;
    height: 38px;
    background: #EEF2F5;
    box-shadow: 0px 1px 2px rgba(8, 11, 13, 0.05);
    border-radius: 6px;
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #444444;
    margin: 24px;
    text-decoration: none;
}

.view-proof-details-btn:hover {
    color: #444444;
    text-decoration: none;
    background: #E0E4E7;
}

::v-deep .card-body {
    padding: 0px !important;
}

.proof-name-row {
    margin-top: 12px;
}

.status-indicator {
    margin-right: 4px;
    height: 6px;
    width: 6px;
}

.status-approved {
    background-color: #019256;
}

.status-not-approved {
    background-color: #EB4654;
}

.status-waiting-for-review {
    background-color: #D9B116;
}

@media (max-width: 500px) {
    .view-proof-details-btn {
        margin-left: 0px !important;
    }

    .proof-status {
        margin-bottom: 16px;
    }
}

@media(min-width: 992px) {
    .job-proof-label {
        width: max-content;
    }
}
</style>
